import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Select,
  Tag,
  Modal,
  Form,
  Radio,
  DatePicker,
  Upload,
  message,
} from 'antd';
import {
  SendOutlined,
  PlusOutlined,
  UploadOutlined,
  VerticalAlignBottomOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ChargingHttpHandler from '../../../shared/chargingHttpHandler';
import secureStorage from '../../../shared/secureStorage';

const sendMethodColor = {
  立即發送: 'blue',
  單次預約: 'orange',
  重複排程: 'purple',
};

const PushTab = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [executingEvents, setExecutingEvents] = useState(new Set()); // 追蹤正在執行的活動
  const [completedEvents, setCompletedEvents] = useState(new Set()); // 追蹤已完成的活動
  const [form] = Form.useForm();
  const [sendMethod, setSendMethod] = useState('immediate');
  const [targetItems, setTargetItems] = useState([]);
  const [isNew, setIsNew] = useState(true);
  const [fileData, setFileData] = useState(null);

  const check = secureStorage.getItem('check');
  // 測試區為 28、29，正式區為 0、1、2、3、4、5、6
  const canPush =
    check !== null &&
    check !== undefined &&
    check !== '' &&
    [0, 1, 2, 3, 4, 5, 6].includes(Number(check));
  const httpHandler = new ChargingHttpHandler(false);
  const chargingAccessToken = secureStorage.getItem('chargingAccessToken');

  const add_search_log = async (success) => {
    const userRole = secureStorage.getItem('userRole');
    // 紀錄搜尋時間
    await httpHandler
      .axios()
      .post(
        '/platform/update_user_log_table',
        {
          log_time: dayjs().format('YYYY/MM/DD HH:mm:ss'),
          user_role: userRole,
          action_type: '推播搜尋',
          action_content: '用戶搜尋推播資訊',
          status: success,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        }
      )
      .catch((e) => {
        console.error('寫入搜尋紀錄失敗', e);
      });
  };

  // 獲取推播活動資料
  const fetchPushEvents = async () => {
    try {
      setLoading(true);

      if (!chargingAccessToken) {
        console.error('未找到充電平台 token');
        return;
      }

      const targetResponse = await httpHandler
        .axios()
        .get('/platform/get_target_customer', {
          headers: {
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        });

      const targetData = targetResponse.data.data;
      const mapData = [
        { value: 0, label: '全部' },
        ...targetData.map((item) => ({
          value: item.id,
          label: item.customer_name,
        })),
      ];
      setTargetItems(mapData);

      const response = await httpHandler.axios().get('/push/get_push_event', {
        headers: {
          Authorization: `Bearer ${chargingAccessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data && response.data.code === 200) {
        await add_search_log(1);
        const events = response.data.data;
        console.log('獲取到的推播活動:', events);

        // 轉換資料格式以符合表格結構
        const transformedData = events.map((event, index) => ({
          key: event.event_id || index,
          eventId: event.actual_id, // 隱藏的 event_id 不要讓user看到
          planName: event.event_name,
          planDescription: event.event_description,
          pushTitle: event.title,
          pushContent: event.content,
          pushUrl: event.url,
          sendTime: event.send_time
            ? new Date(event.send_time).toLocaleString('zh-TW')
            : event.start_date
            ? `${event.start_date} (重複排程)`
            : '立即發送',
          audience: mapData.find(
            (item) => String(item.value) === String(event.audience)
          )?.label, // API 沒有這個欄位，使用預設值
          sendMethod: event.send_time
            ? '單次預約'
            : event.start_date
            ? '重複排程'
            : '立即發送',
          status: event.status === 'sent' ? '已發送' : '等待中',
          createdAt: event.created_at,
          enddate: event.enddate,
        }));

        setData(transformedData);
      } else {
        await add_search_log(0);
        console.error('API 回應格式錯誤:', response.data);
      }
    } catch (error) {
      console.error('獲取推播活動失敗:', error);
      if (error.response?.status === 401) {
        message.error('登入已過期，請重新登入');
        secureStorage.removeItem('chargingAccessToken');
        secureStorage.removeItem('userRole');
      } else {
        message.error('獲取推播活動失敗');
      }
    } finally {
      setLoading(false);
    }
  };

  // 執行推播活動
  const executeEvent = async (keyId, eventId, audience) => {
    try {
      // 設置正在執行狀態
      setExecutingEvents((prev) => new Set(prev).add(keyId));

      if (!chargingAccessToken) {
        message.error('未找到充電平台 token，請重新登入');
        return;
      }

      const targetId = targetItems.find(
        (item) => item.label === audience
      )?.value;

      const postData = { event_id: eventId, target_id: targetId };

      const response = await httpHandler
        .axios()
        .post('/push/schedule_event', postData, {
          headers: {
            Authorization: `Bearer ${chargingAccessToken}`,
            'Content-Type': 'application/json',
          },
        });

      if (response.data && response.data.message === '排程推送事件處理成功') {
        message.success('執行成功！');
        // 設置為已完成狀態
        setCompletedEvents((prev) => new Set(prev).add(keyId));
      } else {
        message.error(response.data?.message || '執行失敗');
      }
    } catch (error) {
      console.error('執行推播活動失敗:', error);
      if (error.response?.status === 401) {
        message.error('登入已過期，請重新登入');
        secureStorage.removeItem('chargingAccessToken');
        secureStorage.removeItem('userRole');
      } else {
        message.error('執行失敗，請稍後再試');
      }
    } finally {
      // 重新載入資料以更新狀態
      fetchPushEvents();
      // 移除正在執行狀態
      setExecutingEvents((prev) => {
        const newSet = new Set(prev);
        newSet.delete(keyId);
        return newSet;
      });
    }
  };

  const executeTest = async (status, keyId, eventId, audience) => {
    try {
      setLoading(true);
      setExecutingEvents((prev) => new Set(prev).add(keyId));

      if (!chargingAccessToken) {
        message.error('未找到充電平台 token，請重新登入');
        return;
      }

      const targetId = targetItems.find(
        (item) => item.label === audience
      )?.value;

      const postData = {
        event_id: eventId,
        target_id: targetId,
        task_status: status,
      };

      await httpHandler
        .axios()
        .post('/push/check_test_exist', postData, {
          headers: {
            Authorization: `Bearer ${chargingAccessToken}`,
            'Content-Type': 'application/json',
          },
        })
        .then(async (response) => {
          if (response.data && response.data.message === '測試人員資料存在') {
            await checkExecuteTest(status, eventId, audience);
          } else {
            message.error(response.data?.message || '不存在測試人員資料');
          }
        });
    } catch (error) {
      console.error('執行推播活動失敗:', error);
      if (error.response?.status === 401) {
        message.error('登入已過期，請重新登入');
        secureStorage.removeItem('chargingAccessToken');
        secureStorage.removeItem('userRole');
      } else {
        message.error('執行失敗，請稍後再試');
      }
    } finally {
      // 移除正在執行狀態
      setExecutingEvents((prev) => {
        const newSet = new Set(prev);
        newSet.delete(keyId);
        return newSet;
      });

      setTimeout(() => {
        // 重新載入資料以更新狀態
        fetchPushEvents();
      }, 3000);
    }
  };

  const checkExecuteTest = async (status, eventId, audience) => {
    const targetId = targetItems.find((item) => item.label === audience)?.value;

    const postData = {
      event_id: eventId,
      target_id: targetId,
      task_status: status,
    };

    const response = await httpHandler
      .axios()
      .post('/push/schedule_event', postData, {
        headers: {
          Authorization: `Bearer ${chargingAccessToken}`,
          'Content-Type': 'application/json',
        },
      });

    if (response.data && response.data.message === '排程推送事件處理成功') {
      message.success('執行成功！');
    } else {
      message.error(response.data?.message || '執行失敗');
    }
  };

  // 下載範例檔
  const DownloadSample = () => {
    const link = document.createElement('a');
    link.href = '/客群名單範例.csv'; // public 資料夾下的檔案路徑
    link.setAttribute('download', '測試人員範例.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = (event_id, audience) => {
    return Modal.confirm({
      title: '確認刪除推播方案？',
      content: '刪除後將無法復原，請確認是否要刪除。',
      okText: '確認',
      cancelText: '取消',
      onOk: async () => {
        try {
          const postData = { ids: event_id, target_id: audience };
          console.log('刪除推播方案資料:', postData);
          const response = await httpHandler
            .axios()
            .post('/push/delete_event', postData, {
              headers: {
                Authorization: `Bearer ${chargingAccessToken}`,
                'Content-Type': 'application/json',
              },
            });

          if (
            response.data &&
            response.data.message === '推播活動資料刪除成功'
          ) {
            message.success('刪除成功！');
          } else {
            message.error(response.data?.message || '刪除失敗');
          }
        } catch (error) {
          console.error('執行推播活動失敗:', error);
          if (error.response?.status === 401) {
            message.error('登入已過期，請重新登入');
            secureStorage.removeItem('chargingAccessToken');
            secureStorage.removeItem('userRole');
          } else {
            message.error('執行失敗，請稍後再試');
          }
        } finally {
          // 重新載入資料以更新狀態
          fetchPushEvents();
        }
      },
    });
  };

  // 組件載入時獲取資料
  useEffect(() => {
    fetchPushEvents();
  }, []);

  const columns = [
    {
      title: '方案名稱',
      dataIndex: 'planName',
      width: 150,
    },
    {
      title: '方案描述',
      dataIndex: 'planDescription',
      width: 200,
      ellipsis: true,
    },
    {
      title: '推播標題',
      dataIndex: 'pushTitle',
      width: 150,
    },
    {
      title: '推播內容',
      dataIndex: 'pushContent',
      width: 200,
      ellipsis: true,
    },
    {
      title: '推播網址',
      dataIndex: 'pushUrl',
      width: 200,
      ellipsis: true,
    },
    {
      title: '發送時間/開始日期',
      dataIndex: 'sendTime',
      width: 150,
    },
    {
      title: '客群名稱',
      dataIndex: 'audience',
      width: 120,
    },
    {
      title: '發送方式',
      dataIndex: 'sendMethod',
      width: 120,
      render: (text) => <Tag color={sendMethodColor[text]}>{text}</Tag>,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      width: 100,
      render: (text) => (
        <Tag color={text === '已發送' ? 'green' : 'orange'}>{text}</Tag>
      ),
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 270,
      render: (_, record) => {
        const isExecuting = executingEvents.has(record.key);
        const isCompleted =
          record.status === '已發送' || completedEvents.has(record.key);

        return (
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() =>
                executeEvent(record.key, record.eventId, record.audience)
              }
              loading={isExecuting}
              disabled={isCompleted}
              style={isCompleted ? {} : isExecuting ? {} : { marginRight: 25 }}>
              {isCompleted ? '執行完成' : isExecuting ? '執行中...' : '執行'}
            </Button>
            <Button
              type="primary"
              size="small"
              loading={isExecuting}
              onClick={() =>
                executeTest(
                  record.status,
                  record.key,
                  record.eventId,
                  record.audience
                )
              }>
              測試
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setIsNew(false);
                setIsModalVisible(true);
                form.setFieldsValue({
                  id: record.eventId,
                  planName: record.planName,
                  planDescription: record.planDescription,
                  pushTitle: record.pushTitle,
                  pushContent: record.pushContent,
                  pushUrl: record.pushUrl,
                  audience: targetItems.find(
                    (item) => item.label === record.audience
                  )?.value,
                  enddate: dayjs(record.enddate),
                });
              }}
              disabled={!canPush || isCompleted}>
              編輯
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() =>
                handleDelete(
                  record.eventId,
                  targetItems.find((item) => item.label === record.audience)
                    ?.value
                )
              }
              disabled={!canPush || isCompleted}>
              刪除
            </Button>
          </Space>
        );
      },
    },
  ];

  const filteredData = data.filter(
    (item) =>
      item.planName?.includes(search) ||
      item.pushTitle?.includes(search) ||
      item.pushContent?.includes(search) ||
      item.planDescription?.includes(search)
  );

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setSendMethod('immediate');
  };

  const handleOk = () => {
    const formdata = new FormData();
    form
      .validateFields()
      .then((values) => {
        // 準備 API 資料
        const apiData = {
          ids: values.id || 0,
          event_name: values.planName,
          event_description: values.planDescription || '',
          title: values.pushTitle,
          content: values.pushContent,
          url: values.pushUrl || '',
          send_time:
            sendMethod === 'single' ? values.sendTime?.toISOString() : '', // 轉換為 UTC 格式
          start_date:
            sendMethod === 'repeat'
              ? values.startDate?.format('YYYY-MM-DD')
              : '',
          end_date:
            sendMethod === 'repeat'
              ? values.endDateType === 'specific'
                ? values.endDate?.format('YYYY-MM-DD')
                : ''
              : '',
          repeat_count:
            sendMethod === 'repeat' && values.endDateType === 'count'
              ? parseInt(values.repeatCount) || 0
              : 0,
          repeat_interval:
            sendMethod === 'repeat'
              ? calculateRepeatInterval(
                  values.repeatIntervalNumber,
                  values.repeatIntervalUnit
                )
              : 0,
          created_at: new Date().toISOString(),
          audience: values.audience,
          enddate: values.enddate?.format('YYYY-MM-DD'),
        };

        formdata.append('apidata', JSON.stringify(apiData));
        if (values.test_file !== undefined) {
          formdata.append('file', values.test_file.file);
        }

        // 調用 API
        callPushEventAPI(formdata);

        setIsModalVisible(false);
        form.resetFields();
        // setSendMethod('immediate');
      })
      .catch((errorInfo) => {
        console.log('Validation failed:', errorInfo);
        message.error('請填寫所有必填欄位！');
      });
  };

  // 計算重複間隔的總分鐘數
  const calculateRepeatInterval = (number, unit) => {
    const num = parseInt(number) || 0;
    switch (unit) {
      case 'days':
        return num * 24 * 60; // 天轉分鐘
      case 'weeks':
        return num * 7 * 24 * 60; // 週轉分鐘
      case 'months':
        return num * 30 * 24 * 60; // 月轉分鐘（假設30天）
      case 'years':
        return num * 365 * 24 * 60; // 年轉分鐘（假設365天）
      default:
        return 0;
    }
  };

  // 調用推播事件 API
  const callPushEventAPI = async (formdata) => {
    try {
      const new_or_not = isNew ? '新增' : '更新';
      if (!chargingAccessToken) {
        message.error('未找到充電平台 token，請重新登入');
        return;
      }

      const new_update = isNew
        ? '/push/push_event_to_db'
        : '/push/update_event_to_db';

      const response = await httpHandler.axios().post(new_update, formdata, {
        headers: {
          'Content-Type': 'multipart/form-data; charset=UTF-8',
          Authorization: `Bearer ${chargingAccessToken}`,
        },
      });

      if (response.data && response.data.success) {
        message.success(`方案${new_or_not}成功！`);
        // 可以選擇重新載入資料或更新本地狀態
        fetchPushEvents(); // 重新載入資料
      } else {
        message.error(response.data?.message || `${new_or_not}失敗`);
      }
    } catch (error) {
      console.error('API 調用失敗:', error);
      if (error.response?.status === 401) {
        message.error('登入已過期，請重新登入');
        // 清除過期的 token
        secureStorage.removeItem('chargingAccessToken');
        secureStorage.removeItem('userRole');
      } else {
        message.error('新增失敗，請稍後再試');
      }
    }
  };

  const handleSendMethodChange = (value) => {
    setSendMethod(value);
  };

  const handleDragger = ({ file, fileList, e }) => {
    setFileData(file);
  };

  return (
    <div
      style={{
        background: '#f7faf9',
        padding: 24,
        borderRadius: 16,
        marginTop: 24,
      }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}>
        <span style={{ fontSize: 32, fontWeight: 700 }}>推播平台</span>
        <Space>
          <Button
            icon={<PlusOutlined />}
            size="large"
            onClick={() => {
              setIsNew(true);
              setIsModalVisible(true);
            }}
            disabled={!canPush}>
            新增
          </Button>
          <Button
            icon={<VerticalAlignBottomOutlined />}
            size="large"
            onClick={DownloadSample}>
            下載測試人員範例
          </Button>
          {/* <Button type="primary" icon={<SendOutlined />} size="large">批量發送</Button> */}
          <Button
            icon={<SendOutlined />}
            size="large"
            onClick={fetchPushEvents}
            loading={loading}>
            重新載入
          </Button>
        </Space>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Select
          defaultValue="所有類型"
          style={{ minWidth: 120 }}
          options={[{ value: 'all', label: '所有類型' }]}
        />
        <Select
          defaultValue="所有狀態"
          style={{ minWidth: 120 }}
          options={[{ value: 'all', label: '所有狀態' }]}
        />
        <Input
          placeholder="搜尋方案名稱、推播標題或內容"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 350 }}
        />
        <Button type="default">搜尋</Button>
      </div>
      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={false}
        bordered={false}
        loading={loading}
        style={{ background: '#fff', borderRadius: 12 }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={isNew ? '新增方案' : '編輯方案'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        width={600}
        okText="確定"
        cancelText="取消">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            sendMethod: 'immediate',
            endDateType: 'specific',
            repeatIntervalNumber: 1,
            repeatIntervalUnit: 'days',
          }}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            label="方案名稱"
            name="planName"
            rules={[{ required: true, message: '請輸入方案名稱' }]}>
            <Input placeholder="請輸入方案名稱" />
          </Form.Item>

          <Form.Item label="方案描述" name="planDescription">
            <Input.TextArea rows={4} placeholder="請輸入方案描述" />
          </Form.Item>

          <Form.Item
            label="方案結束時間"
            name="enddate"
            rules={[{ required: true, message: '請選擇結束時間' }]}>
            <DatePicker
              disabledDate={(current) =>
                current &&
                current.isBefore(dayjs().startOf('day').add(1, 'day'))
              }
              placeholder="請選擇結束時間"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            label="推播標題"
            name="pushTitle"
            rules={[{ required: true, message: '請輸入推播標題' }]}>
            <Input placeholder="請輸入推播標題" />
          </Form.Item>

          <Form.Item
            label="推播內容"
            name="pushContent"
            rules={[{ required: true, message: '請輸入推播內容' }]}>
            <Input.TextArea rows={3} placeholder="請輸入推播內容" />
          </Form.Item>

          <Form.Item label="推播網址" name="pushUrl">
            <Input placeholder="請輸入推播網址" />
          </Form.Item>

          <Form.Item
            label="發送方式"
            name="sendMethod"
            rules={[{ required: true, message: '請選擇發送方式' }]}>
            <Radio.Group
              onChange={(e) => handleSendMethodChange(e.target.value)}>
              <Radio value="immediate">立即發送</Radio>
              <Radio value="single">單次預約</Radio>
              <Radio value="repeat">重複排程</Radio>
            </Radio.Group>
          </Form.Item>

          {sendMethod === 'immediate' && (
            <Form.Item label="發送時間" name="sendTime">
              <Input
                placeholder="立即發送"
                disabled
                style={{ backgroundColor: '#f5f5f5', color: '#999' }}
              />
            </Form.Item>
          )}

          {sendMethod === 'single' && (
            <Form.Item
              label="發送時間"
              name="sendTime"
              rules={[{ required: true, message: '請選擇發送時間' }]}>
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                placeholder="請選擇發送時間"
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}

          {sendMethod === 'repeat' && (
            <>
              <Form.Item
                label="開始日期"
                name="startDate"
                rules={[{ required: true, message: '請選擇開始日期' }]}>
                <DatePicker
                  format="YYYY-MM-DD"
                  placeholder="請選擇開始日期"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                label="結束日期"
                name="endDateType"
                rules={[{ required: true, message: '請選擇結束方式' }]}>
                <Radio.Group>
                  <Radio value="specific">
                    於
                    <Form.Item name="endDate" noStyle>
                      <DatePicker
                        format="YYYY-MM-DD"
                        placeholder="Select date"
                        style={{ width: 150, marginLeft: 8 }}
                      />
                    </Form.Item>
                  </Radio>
                  <Radio value="count">
                    重複次數
                    <Form.Item name="repeatCount" noStyle>
                      <Input
                        type="number"
                        placeholder=""
                        style={{ width: 80, marginLeft: 8 }}
                      />
                    </Form.Item>
                    次
                  </Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label="重複間隔"
                name="repeatInterval"
                rules={[{ required: true, message: '請設定重複間隔' }]}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>每</span>
                  <Form.Item name="repeatIntervalNumber" noStyle>
                    <Input type="number" placeholder="" style={{ width: 80 }} />
                  </Form.Item>
                  <Form.Item name="repeatIntervalUnit" noStyle>
                    <Select placeholder="請..." style={{ width: 120 }}>
                      <Select.Option value="days">天</Select.Option>
                      <Select.Option value="weeks">週</Select.Option>
                      <Select.Option value="months">月</Select.Option>
                      <Select.Option value="years">年</Select.Option>
                    </Select>
                  </Form.Item>
                </div>
              </Form.Item>
            </>
          )}

          <Form.Item
            label="客群設定"
            name="audience"
            rules={[{ required: true, message: '請選擇客群設定' }]}>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={targetItems}
              placeholder="請選擇客群設定"
            />
          </Form.Item>

          <Form.Item label="上傳測試人員" name="test_file">
            <Upload
              name="test_file"
              accept=".csv"
              maxCount={1}
              beforeUpload={() => false}
              onChange={handleDragger}>
              <Button icon={<UploadOutlined />}>上傳</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PushTab;
